import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardBody,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Divider
} from '@chakra-ui/react';
import { Users, FileText, Mail, TrendingUp, Clock, CheckCircle } from 'lucide-react';

interface StatCardProps {
  title: string;
  value: string | number;
  change?: number;
  icon: any;
  colorScheme?: string;
}

const StatCard: React.FC<StatCardProps> = ({ 
  title, 
  value, 
  change, 
  icon: Icon, 
  colorScheme = 'blue' 
}) => (
  <Card bg="gray.800" borderColor="gray.700">
    <CardBody>
      <HStack justify="space-between">
        <VStack align="start" spacing={1}>
          <Text fontSize="sm" color="gray.400" fontWeight="medium">
            {title}
          </Text>
          <Text fontSize="2xl" fontWeight="bold" color="white">
            {value}
          </Text>
          {change !== undefined && (
            <HStack spacing={1}>
              <Text fontSize="sm" color={change > 0 ? 'green.500' : 'red.500'}>
                {change > 0 ? '↗' : '↘'} {Math.abs(change)}%
              </Text>
            </HStack>
          )}
        </VStack>
        <Box
          p={3}
          bg={`${colorScheme}.100`}
          borderRadius="lg"
        >
          <Icon size={24} color={`var(--chakra-colors-${colorScheme}-500)`} />
        </Box>
      </HStack>
    </CardBody>
  </Card>
);

const Dashboard: React.FC = () => {
  // TODO: Replace with real data from API
  const stats = {
    totalContacts: 24,
    newContacts: 6,
    pendingInvoices: 3,
    monthlyRevenue: 15750,
    responseTime: '2.4 hours',
    resolvedToday: 8
  };

  const recentActivity = [
    { id: 1, type: 'contact', message: 'New contact from John Smith', time: '5 min ago', status: 'new' },
    { id: 2, type: 'invoice', message: 'Invoice #CRT-2024-ABC123 sent', time: '1 hour ago', status: 'sent' },
    { id: 3, type: 'email', message: 'Response sent to Sarah Johnson', time: '2 hours ago', status: 'completed' },
    { id: 4, type: 'contact', message: 'Contact marked as resolved', time: '3 hours ago', status: 'resolved' },
  ];

  return (
    <Box>
      {/* Welcome Section */}
      <Box mb={8}>
        <Heading size="lg" mb={2} color="white">Welcome to Clover Portal</Heading>
        <Text color="gray.300">Here's what's happening with your customer service operations</Text>
      </Box>

      {/* Stats Grid */}
      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={6} mb={8}>
        <StatCard
          title="Total Contacts"
          value={stats.totalContacts}
          change={12}
          icon={Users}
          colorScheme="blue"
        />
        <StatCard
          title="New This Week"
          value={stats.newContacts}
          change={8}
          icon={Clock}
          colorScheme="green"
        />
        <StatCard
          title="Pending Invoices"
          value={stats.pendingInvoices}
          icon={FileText}
          colorScheme="orange"
        />
        <StatCard
          title="Monthly Revenue"
          value={`$${stats.monthlyRevenue.toLocaleString()}`}
          change={15}
          icon={TrendingUp}
          colorScheme="purple"
        />
        <StatCard
          title="Avg Response Time"
          value={stats.responseTime}
          change={-18}
          icon={Mail}
          colorScheme="teal"
        />
        <StatCard
          title="Resolved Today"
          value={stats.resolvedToday}
          change={25}
          icon={CheckCircle}
          colorScheme="green"
        />
      </Grid>

      {/* Recent Activity */}
      <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={6}>
        <Card bg="gray.800" borderColor="gray.700">
          <CardBody>
            <Heading size="md" mb={4} color="white">Recent Activity</Heading>
            <VStack spacing={4} align="stretch">
              {recentActivity.map((activity) => (
                <Box key={activity.id}>
                  <HStack justify="space-between" align="start">
                    <VStack align="start" spacing={1} flex={1}>
                      <Text fontSize="sm" fontWeight="medium" color="gray.200">
                        {activity.message}
                      </Text>
                      <Text fontSize="xs" color="gray.400">
                        {activity.time}
                      </Text>
                    </VStack>
                    <Badge
                      colorScheme={
                        activity.status === 'new' ? 'blue' :
                        activity.status === 'sent' ? 'purple' :
                        activity.status === 'completed' ? 'green' :
                        'gray'
                      }
                    >
                      {activity.status}
                    </Badge>
                  </HStack>
                  {activity.id !== recentActivity[recentActivity.length - 1].id && <Divider mt={4} />}
                </Box>
              ))}
            </VStack>
          </CardBody>
        </Card>

        <Card bg="gray.800" borderColor="gray.700">
          <CardBody>
            <Heading size="md" mb={4} color="white">Quick Actions</Heading>
            <VStack spacing={3}>
              <Box
                p={4}
                borderRadius="lg"
                bg="blue.900"
                border="1px dashed"
                borderColor="blue.600"
                w="100%"
                cursor="pointer"
                _hover={{ bg: 'blue.800' }}
                textAlign="center"
              >
                <Text fontSize="sm" fontWeight="medium" color="blue.300">
                  View New Contacts
                </Text>
              </Box>
              <Box
                p={4}
                borderRadius="lg"
                bg="green.900"
                border="1px dashed"
                borderColor="green.600"
                w="100%"
                cursor="pointer"
                _hover={{ bg: 'green.800' }}
                textAlign="center"
              >
                <Text fontSize="sm" fontWeight="medium" color="green.300">
                  Create Invoice
                </Text>
              </Box>
              <Box
                p={4}
                borderRadius="lg"
                bg="purple.900"
                border="1px dashed"
                borderColor="purple.600"
                w="100%"
                cursor="pointer"
                _hover={{ bg: 'purple.800' }}
                textAlign="center"
              >
                <Text fontSize="sm" fontWeight="medium" color="purple.300">
                  Check System Health
                </Text>
              </Box>
            </VStack>
          </CardBody>
        </Card>
      </Grid>
    </Box>
  );
};

export default Dashboard;